import device from './device';
import parseUri from './parseUri';
import useOverlay from '../hooks/useOverlay';
import useLanguage from '../hooks/useLanguage';

export const useAttemptToLaunchBankId = () => {
  const overlay = useOverlay();
  const language = useLanguage();

  return (
    startUrl: string,
    mobileSafariRedirect?: boolean,
    onCancel?: () => void
  ) => {
    const getRedirectUrl = () => {
      // Safari on iOS requires a callback url to be passed to the BankID app,
      // which is used to redirect back to the browser after a finished or cancelled action.
      // Third party browsers on iOS are redirected to using their respective protocols.
      // Android simply redirects to the previous app and the redirect url should be left empty.
      if (device.IS_IOS) {
        switch (device.BROWSER_NAME) {
          case 'Safari':
            return mobileSafariRedirect
              ? `${
                  window.location.href.split('#')[0]
                }#redirect_from_bankid_${Date.now()}`
              : '';
          case 'Chrome Mobile':
            // Yes, "chromes".
            return 'googlechromes://';
          case 'Firefox for iOS':
            return 'firefox://';
        }
      }

      return '';
    };

    const token = parseUri(startUrl).query.get('autostarttoken');
    let redirectUrl = encodeURIComponent(getRedirectUrl());

    // Users will have to manually return to app when running in the Instagram, Facebook or Snapchat in-app browsers on iOS.
    if (device.IS_IOS && device.IS_SOCIAL_MEDIA_IN_APP_BROWSER) {
      redirectUrl = '';
    }

    // The universal link https://app.bankid.com is used for all mobile browsers except on iOS, which uses the bankid:// protocol.
    // The universal link will redirect to the app if installed.
    // But in the case of iOS browsers, the universal link will open in a new tab instead of the app when the request to our backend fetching the `autostarttoken` takes too long (which is often the case) (the reason is that we loose the user interaction and the browser doesn't open the app in this case), which is not the intended behavior.
    // Social media in-app browsers on iOS are a special case, the host app will not be able to open the BankID app using the universal link, but the bankid:// protocol will also not work as the in-app browser will not allow it.
    const url =
      device.IS_IOS && !device.IS_SOCIAL_MEDIA_IN_APP_BROWSER
        ? `bankid:///?autostarttoken=${token}&redirect=${redirectUrl}`
        : device.IS_MOBILE
        ? `https://app.bankid.com/?autostarttoken=${token}&redirect=${redirectUrl}`
        : `bankid:///?autostarttoken=${token}&redirect=`;

    if (device.IS_IOS && device.IS_SOCIAL_MEDIA_IN_APP_BROWSER) {
      // Because of the iOS limitations mentioned above, we show an alert get a user interaction and make the BankID universal link work.
      overlay.presentAlert({
        title: language.get('app_login_se.bankid_same_device_open'),
        items: [
          {
            title: language.get('cancel'),
            onClick: () => {
              onCancel?.();
            },
            isCancel: true,
          },
          {
            title: language.get('app_login_se.bankid_same_device_open.confirm'),
            onClick: () => {
              window.open(url, '_self');
            },
          },
        ],
      });
    } else {
      window.open(url, '_self');
    }
  };
};
